import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { athletesAPI } from '../lib/api';
import { Button } from './ui/button';
import PaywallModal from './PaywallModal';
import { Trash2, AlertTriangle, Users } from 'lucide-react';
import { toast } from 'sonner';

const FREE_ATHLETE_LIMIT = 10;

export default function SubscriptionEnforcementGate({ children }) {
  const { user, userType, isOverLimit, refreshCoachData } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);

  const fetchAthletes = useCallback(async () => {
    setLoadingAthletes(true);
    try {
      const response = await athletesAPI.getAll();
      setAthletes(response.data);
    } catch {
      toast.error('Errore nel caricamento degli atleti');
    } finally {
      setLoadingAthletes(false);
    }
  }, []);

  useEffect(() => {
    if (isOverLimit) {
      fetchAthletes();
    }
  }, [isOverLimit, fetchAthletes]);

  const handleDelete = async (athlete) => {
    setDeletingId(athlete.id);
    try {
      await athletesAPI.delete(athlete.id);
      const updated = athletes.filter((a) => a.id !== athlete.id);
      setAthletes(updated);
      // Aggiorna i dati del coach (athlete_count) nel context
      await refreshCoachData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('lezioni')) {
        toast.error(`Impossibile eliminare ${athlete.name}: è presente in una o più lezioni`);
      } else {
        toast.error(`Impossibile eliminare ${athlete.name}`);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handlePaywallSuccess = async () => {
    await refreshCoachData();
  };

  if (!isOverLimit) return children;

  const excess = (user?.athlete_count ?? athletes.length) - FREE_ATHLETE_LIMIT;

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-4 pt-10 pb-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-7 h-7 text-red-400" />
        </div>
        <h1 className="text-white text-2xl font-black tracking-tight mb-2">
          Piano Pro scaduto
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mx-auto">
          Il tuo abbonamento è terminato. Hai{' '}
          <span className="text-white font-bold">{user?.athlete_count ?? athletes.length} atleti</span>{' '}
          ma il piano gratuito ne consente solo{' '}
          <span className="text-white font-bold">{FREE_ATHLETE_LIMIT}</span>.
        </p>
      </div>

      {/* Contatore progresso */}
      <div className="flex-shrink-0 mx-4 mb-4 bg-zinc-900 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
        <Users className="w-5 h-5 text-zinc-400 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex justify-between items-center mb-1">
            <span className="text-zinc-400 text-xs">Atleti</span>
            <span className="text-white text-xs font-bold">
              {user?.athlete_count ?? athletes.length} / {FREE_ATHLETE_LIMIT}
            </span>
          </div>
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-red-500 rounded-full transition-all duration-300"
              style={{
                width: `${Math.min(((user?.athlete_count ?? athletes.length) / FREE_ATHLETE_LIMIT) * 100, 100)}%`,
              }}
            />
          </div>
        </div>
        {excess > 0 && (
          <span className="text-red-400 text-xs font-bold flex-shrink-0">
            -{excess} richiesti
          </span>
        )}
      </div>

      {/* CTA Rinnova */}
      <div className="flex-shrink-0 px-4 mb-4">
        <Button
          onClick={() => setShowPaywall(true)}
          className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-black text-base rounded-xl py-5"
        >
          Rinnova il piano Pro
        </Button>
      </div>

      {/* Divisore */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 mb-4">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-zinc-500 text-xs">oppure elimina gli atleti in eccesso</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Lista atleti */}
      <div className="flex-1 overflow-y-auto px-4 pb-8">
        {loadingAthletes ? (
          <div className="text-center text-zinc-500 text-sm py-8">Caricamento atleti...</div>
        ) : athletes.length === 0 ? (
          <div className="text-center text-zinc-500 text-sm py-8">Nessun atleta trovato</div>
        ) : (
          <div className="space-y-2">
            {athletes.map((athlete) => (
              <div
                key={athlete.id}
                className="bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{athlete.name}</p>
                  {athlete.email && (
                    <p className="text-zinc-500 text-xs truncate">{athlete.email}</p>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(athlete)}
                  disabled={deletingId === athlete.id}
                  className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-40"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <PaywallModal
        open={showPaywall}
        onOpenChange={setShowPaywall}
        onSuccess={handlePaywallSuccess}
      />
    </div>
  );
}
